/**
 * TopNav - Main top navigation bar
 *
 * Features:
 * - Golden Hour design system
 * - Responsive (desktop + mobile)
 * - Active page highlighting
 * - Sticky on scroll
 * - User avatar/login button
 * - 🌍 Multi-language support
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Home, Users, Newspaper, MessageCircle, Bell, User, CircleDot, Bot, ChevronDown, LogOut, Crown, Settings, Wallet, Search, Heart, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useTranslation, buildLocalizedPath } from '@/i18n';
import { LanguageSwitcher } from '@/i18n/components/LanguageSwitcher';
import ThemeSelector from '@/modules/shared/components/ThemeSelector';
import { useMembershipStatus } from '@/modules/shared/hooks/useMembership';
import { TierBadge } from '@/modules/shared/components/TierBadge';
import { KommendLogo } from '@/modules/shared/components/KommendLogo';

interface NavItem {
  id: string;
  translationKey: string;
  icon: React.ReactNode;
  path: string;
  requireAuth?: boolean;
}

export function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { data: membershipStatus } = useMembershipStatus();
  const currentTier = membershipStatus?.tier ?? 'free';

  // Scroll-aware background: transparent at top, solid on scroll
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 10);
  }, []);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);
  // 构建带语言前缀的路径
  const localizePath = (path: string) => {
    const currentLang = lang || 'en';
    return buildLocalizedPath(currentLang as 'en' | 'zh-CN' | 'ja', path);
  };

  // 导航配置（使用翻译 key）
  const navItems: NavItem[] = [
    {
      id: 'home',
      translationKey: 'navigation.agent.plaza',
      icon: <Home className="w-5 h-5" />,
      path: localizePath('/agent'),
    },
    {
      id: 'plaza',
      translationKey: 'navigation.agent.discovery',
      icon: <Users className="w-5 h-5" />,
      path: localizePath('/agent/plaza'),
    },
    {
      id: 'feed',
      translationKey: 'navigation.main.feed',
      icon: <Newspaper className="w-5 h-5" />,
      path: localizePath('/feed'),
    },
    {
      id: 'circles',
      translationKey: 'navigation.main.circles',
      icon: <CircleDot className="w-5 h-5" />,
      path: localizePath('/circles'),
    },
    // Messages + Notifications moved into the user-menu dropdown (cleaner top nav)
  ];

  const currentPathWithoutLang = location.pathname.replace(/^\/(en|zh-CN|ja)/, '');
  const isFeedPage = currentPathWithoutLang.startsWith('/feed');
  const isStudioPage =
    currentPathWithoutLang.startsWith('/studio') ||
    currentPathWithoutLang.startsWith('/me/monetization') ||
    currentPathWithoutLang.startsWith('/help/creator-studio');
  const isActive = (path: string) => {
    // 移除语言前缀进行比较
    const pathWithoutLang = path.replace(/^\/(en|zh-CN|ja)/, '');
    
    if (pathWithoutLang === '/agent') {
      return currentPathWithoutLang === '/' || currentPathWithoutLang === '/agent' || currentPathWithoutLang === '/agent/home';
    }
    return currentPathWithoutLang.startsWith(pathWithoutLang);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isFeedPage
          ? 'bg-[var(--y-bg-secondary)] border-b border-[var(--y-border)] shadow-[var(--y-shadow-hd)]'
          : isStudioPage
            ? 'bg-[var(--y-surface-primary)]/62 border-b border-[var(--y-border)] shadow-[var(--y-shadow-hd)] backdrop-blur-xl'
          : scrolled
            ? 'bg-[var(--y-surface-primary)] border-b border-[var(--y-border)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate(localizePath('/agent'))}
          >
            <KommendLogo size={32} />
            <span className="text-xl font-bold text-[var(--y-accent)]">
              Kommend
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              // Skip auth-required items if not logged in
              if (item.requireAuth && !user) return null;

              const active = isActive(item.path);

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                    transition-all duration-200
                    ${active
                      ? 'bg-[var(--y-accent)] text-[var(--y-accent-text)]'
                      : 'text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)] hover:text-[var(--y-text-primary)]'
                    }
                  `}
                  aria-label={t(item.translationKey)}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.icon}
                  <span>{t(item.translationKey)}</span>
                </button>
              );
            })}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden items-center gap-2">
            {navItems.filter(i => ['home', 'feed', 'circles'].includes(i.id)).map((item) => {
              const active = isActive(item.path);

              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`
                    p-2 rounded-lg transition-colors
                    ${active
                      ? 'bg-[var(--y-accent)] text-[var(--y-accent-text)]'
                      : 'text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)]'
                    }
                  `}
                  aria-label={t(item.translationKey)}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.icon}
                </button>
              );
            })}
          </div>

          {/* Right Section: Search + Language Switcher + User */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search — quick entry to the search page */}
            <button
              onClick={() => navigate(localizePath('/search'))}
              className="p-2 rounded-lg text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)] hover:text-[var(--y-text-primary)] transition-colors"
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>

            <ThemeSelector />

            {/* Settings — no auth required */}
            <button
              onClick={() => navigate(localizePath('/settings'))}
              className="p-2 rounded-lg text-[var(--y-text-secondary)] hover:bg-[var(--y-surface-secondary)] hover:text-[var(--y-text-primary)] transition-colors"
              aria-label="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>

            {/* Language Switcher - Minimal variant */}
            <LanguageSwitcher 
              variant="minimal" 
              size="sm"
              className="hidden sm:flex"
            />
            
            {/* Mobile language switcher - 只显示下拉按钮 */}
            <LanguageSwitcher 
              variant="minimal" 
              size="sm"
              className="sm:hidden"
            />

            {/* Pricing link - Desktop only, for non-authenticated users */}
            {!user && (
              <button
                onClick={() => navigate(localizePath('/membership'))}
                className="hidden md:inline-flex text-sm font-medium text-[var(--y-text-secondary)] hover:text-[var(--y-accent)] transition-colors"
              >
                Pricing
              </button>
            )}

            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--y-surface-secondary)] transition-colors"
                  aria-label="User menu"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  <div className="w-8 h-8 rounded-full bg-[var(--y-accent)] flex items-center justify-center">
                    <User className="w-5 h-5 text-[var(--y-accent-text)]" />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-[var(--y-text-primary)]">
                    {user.username || t('navigation.agent.myAgent')}
                  </span>
                  {currentTier !== 'free' && (
                    <TierBadge tier={currentTier} size="xs" />
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[var(--y-text-secondary)] transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-1 w-52 bg-[var(--y-surface-primary)] border border-[var(--y-border)] rounded-xl shadow-lg overflow-hidden z-50">
                    <button
                      onClick={() => { navigate(localizePath(`/user/${user.id}`)); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <User className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('navigation.drawer.myProfile')}
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/agent/me')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <Bot className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('navigation.drawer.myAgent')}
                    </button>
                    <div className="border-t border-[var(--y-border)]" />
                    {/* Messages + Notifications — relocated here from the top nav bar */}
                    <button
                      onClick={() => { navigate(localizePath('/messages')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('navigation.main.messages')}
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/notifications')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <Bell className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('navigation.main.notifications')}
                    </button>
                    <div className="border-t border-[var(--y-border)]" />
                    <button
                      onClick={() => { navigate(localizePath('/membership')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <Crown className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      <span className="flex-1 text-left">{t('navigation.drawer.membership')}</span>
                      <TierBadge tier={currentTier} size="xs" />
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/account')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <Wallet className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('navigation.account')}
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/studio')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <LayoutDashboard className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('creatorSubscription.studioShell.title')}
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/me/subscriptions')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <Heart className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('creatorSubscription.fan.mySubscriptions')}
                    </button>
                    <button
                      onClick={() => { navigate(localizePath('/me/circles')); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[var(--y-text-primary)] hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <CircleDot className="w-4 h-4 text-[var(--y-text-secondary)]" />
                      {t('circles.my.pageTitle')}
                    </button>
                    <div className="border-t border-[var(--y-border)]" />
                    <button
                      onClick={async () => { setShowUserMenu(false); await signOut(); navigate(localizePath('/')); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-[var(--y-surface-secondary)] transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('navigation.userMenu.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-login-modal'))}
                className="px-4 py-2 rounded-lg bg-[var(--y-accent)] text-[var(--y-accent-text)] font-medium text-sm hover:opacity-90 transition-opacity"
                aria-label={t('navigation.userMenu.login')}
              >
                {t('navigation.userMenu.login')}
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
